import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import api from '../../services/api';
import toast from '../../utils/toast';
import { FaArrowLeft, FaSave, FaCheck, FaTimes, FaChevronDown, FaChevronRight, FaFolder, FaFile } from 'react-icons/fa';

function getDocGenBasePath(pathname) {
  if (pathname.includes('/admin/settings/admin-document-generation')) {
    return '/admin/settings/admin-document-generation';
  }
  return '/admin/admin-document-generation';
}

const FieldMapping = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const docGenBase = getDocGenBasePath(location.pathname);
  const [template, setTemplate] = useState(null);
  const [availableModels, setAvailableModels] = useState({});
  const [fieldMappings, setFieldMappings] = useState([]);
  const [selectedModel, setSelectedModel] = useState('');
  const [expandedFields, setExpandedFields] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [selectedFieldIndex, setSelectedFieldIndex] = useState(0);

  useEffect(() => {
    loadTemplateAndModels();
  }, [id]);

  // Add useEffect to monitor fieldMappings changes
  useEffect(() => {
    console.log('fieldMappings state updated:', fieldMappings);
  }, [fieldMappings]);

  // Add useEffect to monitor selectedFieldIndex changes
  useEffect(() => {
    console.log('selectedFieldIndex changed to:', selectedFieldIndex);
  }, [selectedFieldIndex]);

  const loadTemplateAndModels = async () => {
    try {
      setLoading(true);
      
      // Load template
      const templateResponse = await api.get(`/document-generation/templates/${id}`);
      const templateData = templateResponse.data.data;
      setTemplate(templateData);
      
      // Set selected model if already configured
      if (templateData.objectMapping && templateData.objectMapping.modelName) {
        setSelectedModel(templateData.objectMapping.modelName);
      }
      
      // Initialize field mappings
      let initialMappings;
      if (templateData.fieldMappings && templateData.fieldMappings.length > 0) {
        // Use existing field mappings
        initialMappings = templateData.fieldMappings;
      } else {
        // Initialize from extracted fields if no mappings exist
        initialMappings = templateData.extractedFields?.map(field => ({
          templateField: field.fieldName,
          fieldPath: '',
          fieldType: 'string',
          description: '',
          isRequired: false,
          defaultValue: ''
        })) || [];
      }
      setFieldMappings(initialMappings);
      
      // Load available models
      const modelsResponse = await api.get('/document-generation/models');
      setAvailableModels(modelsResponse.data.data);
      
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load template and models');
    } finally {
      setLoading(false);
    }
  };

  const handleModelSelect = (modelName) => {
    setSelectedModel(modelName);
    // Reset all field mappings when model changes
    const resetMappings = fieldMappings.map(mapping => ({
      ...mapping,
      fieldPath: ''
    }));
    setFieldMappings(resetMappings);
  };

  const handleFieldMappingChange = (index, field, value) => {
    console.log('handleFieldMappingChange called with:', { index, field, value });
    setFieldMappings(prevMappings => {
      const updatedMappings = [...prevMappings];
      updatedMappings[index] = {
        ...updatedMappings[index],
        [field]: value
      };
      console.log('Updated mappings:', updatedMappings);
      return updatedMappings;
    });
  };

  const toggleFieldExpansion = (fieldPath) => {
    setExpandedFields(prev => ({
      ...prev,
      [fieldPath]: !prev[fieldPath]
    }));
  };

  const renderFieldTree = (fields, parentPath = '') => {
    return Object.entries(fields).map(([fieldName, fieldInfo]) => {
      const fullPath = parentPath ? `${parentPath}.${fieldName}` : fieldName;
      const isExpanded = expandedFields[fullPath];
      const hasChildren = fieldInfo.type === 'object' && fieldInfo.fields;
      const isArray = fieldInfo.type === 'array';
      
      return (
        <div key={fullPath} className="ml-4">
          <div className="flex items-center py-1">
            {hasChildren || isArray ? (
              <button
                onClick={() => toggleFieldExpansion(fullPath)}
                className="mr-2 text-gray-500 hover:text-gray-700"
              >
                {isExpanded ? <FaChevronDown size={12} /> : <FaChevronRight size={12} />}
              </button>
            ) : (
              <div className="w-4 mr-2" />
            )}
            <span className="mr-2">
              {hasChildren ? <FaFolder size={12} className="text-blue-500" /> : 
               isArray ? <FaFolder size={12} className="text-purple-500" /> :
               <FaFile size={12} className="text-gray-500" />}
            </span>
            <button
              onClick={() => handleFieldSelect(fullPath, fieldInfo)}
              className="text-left hover:bg-blue-50 px-2 py-1 rounded flex-1"
            >
              <span className="font-medium">{fieldName}</span>
              <span className="text-sm text-gray-500 ml-2">({fieldInfo.type})</span>
              {isArray && (
                <span className="text-xs text-purple-600 ml-2">[Array]</span>
              )}
              {fieldInfo.description && (
                <span className="text-xs text-gray-400 block">{fieldInfo.description}</span>
              )}
            </button>
          </div>
          
          {/* Show array elements if expanded */}
          {isArray && isExpanded && fieldInfo.itemType === 'object' && fieldInfo.fields && (
            <div className="border-l border-gray-200 ml-2">
              <div className="ml-4 py-1">
                <div className="text-xs text-purple-600 font-medium mb-2">Array Elements (e.g., {fieldName}[0]):</div>
                {Object.entries(fieldInfo.fields).map(([subFieldName, subFieldInfo]) => {
                  const arrayElementPath = `${fullPath}[i].${subFieldName}`;
                  return (
                    <div key={arrayElementPath} className="ml-4 py-1">
                      <div className="flex items-center">
                        <span className="mr-2">
                          <FaFile size={12} className="text-gray-500" />
                        </span>
                        <button
                          onClick={() => handleFieldSelect(arrayElementPath, subFieldInfo)}
                          className="text-left hover:bg-blue-50 px-2 py-1 rounded flex-1"
                        >
                          <span className="font-medium">{subFieldName}</span>
                          <span className="text-sm text-gray-500 ml-2">({subFieldInfo.type})</span>
                          <span className="text-xs text-purple-600 ml-2">[{fieldName}[i].{subFieldName}]</span>
                          {subFieldInfo.description && (
                            <span className="text-xs text-gray-400 block">{subFieldInfo.description}</span>
                          )}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Show nested object fields if expanded */}
          {hasChildren && isExpanded && (
            <div className="border-l border-gray-200 ml-2">
              {renderFieldTree(fieldInfo.fields, fullPath)}
            </div>
          )}
        </div>
      );
    });
  };

  const handleFieldSelect = (fieldPath, fieldInfo) => {
    console.log('handleFieldSelect called with:', { fieldPath, fieldInfo, selectedFieldIndex });
    if (selectedFieldIndex !== null && selectedFieldIndex >= 0) {
      console.log('Updating field mapping for index:', selectedFieldIndex);
      
      handleFieldMappingChange(selectedFieldIndex, 'fieldPath', fieldPath);
      handleFieldMappingChange(selectedFieldIndex, 'description', fieldInfo.description || '');
      
      console.log('Field mapping updated successfully');
    } else {
      console.log('No field selected or invalid selectedFieldIndex:', selectedFieldIndex);
    }
  };

  const handleArrayIndexChange = (index, arrayIndex) => {
    const mapping = fieldMappings[index];
    if (mapping.fieldPath) {
      // Check if the field path contains an array element with any index pattern
      const arrayMatch = mapping.fieldPath.match(/^(.+)\[([^\]]+)\]\.(.+)$/);
      if (arrayMatch) {
        const [, arrayField, currentIndex, subField] = arrayMatch;
        // Use arrayIndex if it's not null/undefined/empty string, otherwise use 'i'
        const newPath = arrayIndex !== null && arrayIndex !== undefined && arrayIndex !== '' 
          ? `${arrayField}[${arrayIndex}].${subField}` 
          : `${arrayField}[i].${subField}`;
        handleFieldMappingChange(index, 'fieldPath', newPath);
      }
    }
  };

  // Helper function to get array index from field path
  const getArrayIndex = (fieldPath) => {
    const match = fieldPath.match(/\[(\d+)\]/);
    return match ? match[1] : '';
  };

  // Helper function to check if field path is an array element
  const isArrayElement = (fieldPath) => {
    return fieldPath.includes('[') && fieldPath.includes('].');
  };

  const handleSaveMappings = async () => {
    try {
      setSaving(true);
      
      // Validate object mapping
      if (!selectedModel) {
        toast.error('Please select an object type for this document');
        return;
      }
      
      // Validate field mappings
      const errors = [];
      fieldMappings.forEach((mapping, index) => {
        if (mapping.templateField) {
          // Check if mapped fields have valid field paths
          if (mapping.fieldPath && !mapping.templateField) {
            errors.push(`Invalid mapping: field path "${mapping.fieldPath}" has no template field`);
          }
        }
      });
      
      if (errors.length > 0) {
        toast.error(errors.join(', '));
        return;
      }
      
      // Filter out empty mappings
      const validMappings = fieldMappings.filter(mapping => 
        mapping.templateField && mapping.fieldPath
      );
      
      const objectMapping = {
        modelName: selectedModel,
        description: availableModels[selectedModel]?.description || ''
      };
      
      await api.put(`/document-generation/templates/${id}/mappings`, {
        objectMapping,
        fieldMappings: validMappings
      });
      
      toast.success('Field mappings saved successfully');
      navigate(docGenBase);
      
    } catch (error) {
      console.error('Error saving mappings:', error);
      toast.error('Failed to save field mappings');
    } finally {
      setSaving(false);
    }
  };

  const handleBack = () => {
    navigate(docGenBase);
  };

  const getFieldType = (fieldPath, modelName) => {
    if (!modelName || !availableModels[modelName]) return 'string';
    
    const fields = availableModels[modelName].fields;
    const fieldInfo = fields[fieldPath];
    return fieldInfo ? fieldInfo.type : 'string';
  };

  const getFieldDescription = (fieldPath, modelName) => {
    if (!modelName || !availableModels[modelName]) return '';
    
    const fields = availableModels[modelName].fields;
    const fieldInfo = fields[fieldPath];
    return fieldInfo ? fieldInfo.description : '';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!template) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">Template not found</p>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Field Mapping</h1>
          <p className="text-gray-600">Map template fields to database object fields</p>
        </div>
        <div className="flex space-x-2">
          <button
            onClick={handleBack}
            className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            <FaArrowLeft className="mr-2" />
            Back
          </button>
          <button
            onClick={handleSaveMappings}
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
          >
            <FaSave className="mr-2" />
            {saving ? 'Saving...' : 'Save Mappings'}
          </button>
        </div>
      </div>

      {/* Template Information */}
      <div className="bg-white p-6 rounded-lg shadow">
        <h2 className="text-lg font-medium text-gray-900 mb-4">Template Information</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">Template Name</label>
            <p className="text-sm text-gray-900">{template.name}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Category</label>
            <p className="text-sm text-gray-900">{template.category}</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">Extracted Fields</label>
            <p className="text-sm text-gray-900">{template.extractedFields?.length || 0} fields</p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">File</label>
            <p className="text-sm text-gray-900">{template.originalFileName}</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Object Selection */}
        <div className="bg-white p-6 rounded-lg shadow">
          <h2 className="text-lg font-medium text-gray-900 mb-4">1. Select Object Type</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Choose the database object type for this document
              </label>
              <select
                value={selectedModel}
                onChange={(e) => handleModelSelect(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Select an object type...</option>
                {Object.entries(availableModels).map(([modelName, modelInfo]) => (
                  <option key={modelName} value={modelName}>
                    {modelInfo.name} - {modelInfo.description}
                  </option>
                ))}
              </select>
            </div>
            
            {selectedModel && availableModels[selectedModel] && (
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="text-sm font-medium text-gray-900 mb-2">Available Fields</h3>
                <div className="text-xs text-gray-600 mb-3">
                  <p>💡 <strong>How to map fields:</strong></p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li><strong>Select a template field</strong> by clicking its radio button (highlighted in blue)</li>
                    <li><strong>Click on any field in the tree</strong> to map it to the selected template field</li>
                    <li>Fields with <span className="text-purple-600">[Array]</span> are arrays - expand to see array elements like <code>authorizedSignatories[i].designationName</code></li>
                    <li>Fields with <span className="text-blue-500">📁</span> are objects - click to expand and see nested fields</li>
                    <li>Fields with <span className="text-gray-500">📄</span> are simple fields - click to map</li>
                    <li>For array elements, you can specify an index (e.g., <code>authorizedSignatories[0].designationName</code>)</li>
                  </ul>
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {renderFieldTree(availableModels[selectedModel].fields)}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Field Mappings */}
        <div className="bg-white p-6 rounded-lg shadow" key={JSON.stringify(fieldMappings)}>
          <h2 className="text-lg font-medium text-gray-900 mb-4">2. Map Template Fields</h2>
          <div className="space-y-4">
            {fieldMappings.map((mapping, index) => (
              <div
                key={index}
                className={`border border-gray-200 rounded-lg p-4 ${selectedFieldIndex === index ? 'ring-2 ring-blue-400' : ''}`}
                onClick={() => setSelectedFieldIndex(index)}
                style={{ cursor: 'pointer' }}
              >
                <div className="flex justify-between items-start mb-2">
                  <div className="flex items-center space-x-2">
                    <input
                      type="radio"
                      checked={selectedFieldIndex === index}
                      onChange={() => setSelectedFieldIndex(index)}
                      className="form-radio text-blue-600"
                    />
                    <h3 className="text-sm font-medium text-gray-900">
                      Template Field: <span className="font-mono bg-gray-100 px-2 py-1 rounded">[{mapping.templateField}]</span>
                    </h3>
                  </div>
                  {mapping.fieldPath && (
                    <span className="inline-flex items-center px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-800">
                      <FaCheck className="mr-1" />
                      Mapped
                    </span>
                  )}
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-1">
                      Database Field Path
                    </label>
                    <div className="flex space-x-2">
                      <input
                        type="text"
                        value={mapping.fieldPath}
                        readOnly
                        placeholder="Select a field from the tree above"
                        className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm bg-gray-50 text-gray-700 cursor-not-allowed"
                      />
                      {mapping.fieldPath && isArrayElement(mapping.fieldPath) && (
                        <div className="flex items-center space-x-1">
                          <span className="text-xs text-gray-500">Index:</span>
                          <input
                            type="number"
                            min="0"
                            placeholder="0"
                            value={getArrayIndex(mapping.fieldPath)}
                            onChange={(e) => handleArrayIndexChange(index, e.target.value)}
                            className="w-16 border border-gray-300 rounded-md px-2 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                          />
                        </div>
                      )}
                    </div>
                    {mapping.fieldPath && (
                      <p className="text-xs text-gray-500 mt-1">
                        Type: {getFieldType(mapping.fieldPath, selectedModel)} | 
                        {getFieldDescription(mapping.fieldPath, selectedModel)}
                      </p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldMapping; 